import { withVersion } from '../constants.js';

export class LayersPanel {
    constructor(doc, bus, undoManager) {
        this.doc = doc;
        this.bus = bus;
        this.undoManager = undoManager;

        this.list = document.getElementById('layers-list');
        this._opacityRow = document.getElementById('layer-opacity-row');
        this._opacitySlider = document.getElementById('layer-opacity-slider');
        this._opacityNum = document.getElementById('layer-opacity-num');
        this._opacityBefore = null;
        this._lastClickTime = 0;
        this._lastClickIndex = -1;

        // Name filter. Display-only: it never touches the document, so indices
        // stay the real layer indices and every action keeps working on them.
        this._filter = '';
        this._filterInput = document.getElementById('layer-filter');
        this._filterCount = document.getElementById('layer-filter-count');

        // Thumbnails are drawn lazily, only for rows actually on screen.
        // render() runs on every layer-changed — i.e. on every brush stroke —
        // and compositing a thumbnail per layer made that cost scale with the
        // layer count: at 200 layers it was 200 composites per stroke, all but
        // a dozen of them for rows scrolled out of sight.
        this._thumbRows = [];
        this._thumbDrawn = new Set();
        this.list.addEventListener('scroll', () => this._drawVisibleThumbs());

        this._filterInput.addEventListener('input', () => {
            this._filter = this._filterInput.value.trim().toLowerCase();
            this.render();
        });
        this._filterInput.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') { e.preventDefault(); this._clearFilter(); }
            // Tool shortcuts are already guarded against inputs, but Enter and
            // the arrows would otherwise reach the document behind the panel.
            e.stopPropagation();
        });
        document.getElementById('layer-filter-clear').addEventListener('click', () => {
            this._clearFilter();
            this._filterInput.focus();
        });

        document.getElementById('layer-add-btn').addEventListener('click', () => this._addLayer());
        document.getElementById('layer-del-btn').addEventListener('click', () => this._deleteLayer());
        document.getElementById('layer-up-btn').addEventListener('click', () => this._moveLayer(-1));
        document.getElementById('layer-down-btn').addEventListener('click', () => this._moveLayer(1));
        document.getElementById('layer-dup-btn').addEventListener('click', () => this._duplicateLayer());

        this._opacitySlider.addEventListener('pointerdown', () => {
            this._opacityBefore = this.doc.getActiveLayer().opacity;
        });
        this._opacitySlider.addEventListener('input', () => {
            const val = parseInt(this._opacitySlider.value);
            this._opacityNum.value = val;
            this.doc.getActiveLayer().opacity = val / 100;
            this.bus.emit('layer-changed');
        });
        this._opacitySlider.addEventListener('pointerup', () => {
            const layer = this.doc.getActiveLayer();
            if (this._opacityBefore !== null && layer.opacity !== this._opacityBefore) {
                this.undoManager.pushEntry({
                    type: 'layer-opacity',
                    layerIndex: this.doc.activeLayerIndex,
                    beforeOpacity: this._opacityBefore,
                    afterOpacity: layer.opacity,
                });
            }
            this._opacityBefore = null;
        });
        this._opacityNum.addEventListener('focus', () => {
            this._opacityBefore = this.doc.getActiveLayer().opacity;
        });
        this._opacityNum.addEventListener('change', () => {
            const val = Math.max(0, Math.min(100, parseInt(this._opacityNum.value) || 100));
            this._opacitySlider.value = val;
            this._opacityNum.value = val;
            const layer = this.doc.getActiveLayer();
            layer.opacity = val / 100;
            if (this._opacityBefore !== null && layer.opacity !== this._opacityBefore) {
                this.undoManager.pushEntry({
                    type: 'layer-opacity',
                    layerIndex: this.doc.activeLayerIndex,
                    beforeOpacity: this._opacityBefore,
                    afterOpacity: layer.opacity,
                });
            }
            this._opacityBefore = null;
            this.bus.emit('layer-changed');
        });

        // Playback ticks emit layer-changed; the layer list has nothing new to
        // show for it (same layers, same names, same order) and rebuilding it
        // ten times a second is what makes it flicker. Thumbnails do go stale
        // while playing, so one render on the way out catches them up.
        this._playing = false;
        this.bus.on('playback-changed', (playing) => {
            this._playing = playing;
            if (!playing) this.render();
        });

        this.bus.on('layer-changed', () => { if (!this._playing) this.render(); });
        this.bus.on('document-changed', () => { if (!this._playing) this.render(); });
        this.bus.on('active-layer-changed', () => this.render());

        this.render();
    }

    _clearFilter() {
        this._filterInput.value = '';
        this._filter = '';
        this.render();
    }

    _matchesFilter(layer) {
        return !this._filter || layer.name.toLowerCase().includes(this._filter);
    }

    _snapshotMeta() {
        return {
            activeIndex: this.doc.activeLayerIndex,
            selected: new Set(this.doc.selectedLayerIndices),
            anim: this.doc.snapshotAnimation(),
        };
    }

    render() {
        // Sync opacity controls
        const activeLayer = this.doc.getActiveLayer();
        const multiSelected = this.doc.selectedLayerIndices.size >= 2;
        if (activeLayer && !multiSelected) {
            const pct = Math.round(activeLayer.opacity * 100);
            this._opacitySlider.value = pct;
            this._opacityNum.value = pct;
            this._opacityRow.classList.remove('disabled');
        } else {
            this._opacityRow.classList.add('disabled');
        }

        // Wiping the list collapses its scrollHeight, which makes the browser
        // clamp scrollTop to 0 — so a scrolled list jumps to the top on every
        // render, not just on a tab switch but on every brush stroke too
        // (those emit layer-changed as well). Capture it, restore it after the
        // items are back. A shorter list clamps on its own, which is correct.
        const scrollTop = this.list.scrollTop;
        this.list.innerHTML = '';

        const total = this.doc.layers.length;
        const matches = this._filter
            ? this.doc.layers.filter(l => this._matchesFilter(l)).length
            : total;
        this._filterCount.textContent = this._filter ? `${matches}/${total}` : '';

        const rows = [];

        // Render layers top-to-bottom (reverse of array order, since array[0] = bottom)
        for (let i = this.doc.layers.length - 1; i >= 0; i--) {
            const layer = this.doc.layers[i];
            const isActive = i === this.doc.activeLayerIndex;
            const matched = this._matchesFilter(layer);
            // The active layer stays visible even when it doesn't match: Delete,
            // Move and Duplicate all act on it, and operating on a row you can't
            // see is how you lose work. It's dimmed to show it's not a match.
            if (!matched && !isActive) continue;

            const item = document.createElement('div');
            const isSelected = this.doc.selectedLayerIndices.has(i);
            item.className = 'layer-item' + (isActive ? ' active' : '') + (isSelected ? ' selected' : '')
                + (matched ? '' : ' filtered-out');

            // Visibility toggle
            const vis = document.createElement('div');
            vis.className = 'layer-visibility' + (layer.visible ? '' : ' hidden');
            const visIcon = document.createElement('img');
            visIcon.src = withVersion(layer.visible ? 'images/icon-eye.svg' : 'images/icon-eye-off.svg');
            visIcon.draggable = false;
            vis.appendChild(visIcon);
            vis.addEventListener('click', (e) => {
                e.stopPropagation();
                const beforeStates = this.doc.layers.map(l => l.visible);
                layer.visible = !layer.visible;
                const afterStates = this.doc.layers.map(l => l.visible);
                this.undoManager.pushEntry({
                    type: 'layer-visibility',
                    beforeStates,
                    afterStates,
                });
                this.bus.emit('layer-changed');
            });
            vis.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const beforeStates = this.doc.layers.map(l => l.visible);
                const isSolo = this.doc.layers.every(l => l === layer ? l.visible : !l.visible);
                for (const l of this.doc.layers) l.visible = isSolo ? true : (l === layer);
                const afterStates = this.doc.layers.map(l => l.visible);
                this.undoManager.pushEntry({
                    type: 'layer-visibility',
                    beforeStates,
                    afterStates,
                });
                this.bus.emit('layer-changed');
            });

            // Thumbnail. Left blank here; _drawVisibleThumbs() fills in the
            // ones that are actually in view, and the scroll handler catches
            // the rest as they appear.
            let thumb;
            if (layer.type === 'text') {
                thumb = document.createElement('div');
                thumb.className = 'layer-thumbnail layer-text-icon';
                thumb.textContent = 'T';
            } else {
                thumb = document.createElement('canvas');
                thumb.className = 'layer-thumbnail';
                thumb.width = 32;
                thumb.height = 32;
                rows.push({ item, canvas: thumb, layer });
            }

            // Name
            const name = document.createElement('span');
            name.className = 'layer-name';
            name.textContent = layer.name;

            item.addEventListener('click', (e) => {
                const now = Date.now();
                if (now - this._lastClickTime < 400 && this._lastClickIndex === i) {
                    this._lastClickTime = 0;
                    this._startRename(name, layer, i);
                    return;
                }
                this._lastClickTime = now;
                this._lastClickIndex = i;

                if (e.ctrlKey) {
                    // Can't deselect the active layer — it's always selected
                    if (i !== this.doc.activeLayerIndex) {
                        const sel = this.doc.selectedLayerIndices;
                        if (sel.has(i)) {
                            sel.delete(i);
                        } else {
                            sel.add(i);
                        }
                    }
                    this.bus.emit('layer-changed');
                } else {
                    this.doc.selectedLayerIndices.clear();
                    this.doc.selectedLayerIndices.add(i);
                    this.doc.activeLayerIndex = i;
                    this.bus.emit('active-layer-changed');
                }
                this.render();
            });

            item.appendChild(vis);
            item.appendChild(thumb);
            item.appendChild(name);
            this.list.appendChild(item);
        }

        this.list.scrollTop = scrollTop;

        // Scroll position first: which rows count as visible depends on it.
        this._thumbRows = rows;
        this._thumbDrawn.clear();
        this._drawVisibleThumbs();
    }

    // Draw thumbnails for the rows inside the scroll viewport (plus a few rows
    // of slack either side) that aren't already drawn. Rows are uniform height,
    // so the visible range is arithmetic rather than 200 offsetTop reads.
    _drawVisibleThumbs() {
        const rows = this._thumbRows;
        if (!rows.length) return;

        let first = 0;
        let last = rows.length - 1;
        const rowHeight = rows[0].item.offsetHeight;
        const viewHeight = this.list.clientHeight;
        // Both are 0 while the panel is hidden or not laid out yet; drawing
        // everything is the safe fallback, and it's what used to happen anyway.
        if (rowHeight > 0 && viewHeight > 0) {
            const SLACK = 3;
            const top = this.list.scrollTop;
            first = Math.max(0, Math.floor(top / rowHeight) - SLACK);
            last = Math.min(last, Math.ceil((top + viewHeight) / rowHeight) + SLACK);
            // A browser clamps scrollTop when the content shrinks under it (say
            // the filter narrows a list that was scrolled to the bottom), but
            // don't lean on that: an out-of-range window would silently draw
            // nothing at all.
            first = Math.min(first, last);
        }

        for (let k = first; k <= last; k++) {
            if (this._thumbDrawn.has(k)) continue;
            this._drawThumbnail(rows[k].canvas, rows[k].layer);
            this._thumbDrawn.add(k);
        }
    }

    // #layers-list is a single DOM node shared by every tab, so its scroll
    // offset belongs to the tab rather than to this panel — each document has
    // its own layer stack. app.js persists it next to zoom/pan.
    getScrollTop() {
        return this.list.scrollTop;
    }

    setScrollTop(value) {
        this.list.scrollTop = value || 0;
    }

    _drawThumbnail(canvas, layer) {
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, 32, 32);

        const docW = this.doc.width;
        const docH = this.doc.height;

        // Fit document ratio into 32x32 thumbnail
        const scale = Math.min(32 / docW, 32 / docH);
        const dw = Math.round(docW * scale);
        const dh = Math.round(docH * scale);
        const dx = Math.round((32 - dw) / 2);
        const dy = Math.round((32 - dh) / 2);

        // Visible portion of the layer within document bounds
        const lx0 = Math.max(0, layer.offsetX);
        const ly0 = Math.max(0, layer.offsetY);
        const lx1 = Math.min(docW, layer.offsetX + layer.width);
        const ly1 = Math.min(docH, layer.offsetY + layer.height);
        const vw = lx1 - lx0;
        const vh = ly1 - ly0;
        if (vw <= 0 || vh <= 0) return;

        const imgData = new ImageData(vw, vh);
        const buf = imgData.data;
        for (let y = 0; y < vh; y++) {
            for (let x = 0; x < vw; x++) {
                const lx = (lx0 - layer.offsetX) + x;
                const ly = (ly0 - layer.offsetY) + y;
                const colorIndex = layer.data[ly * layer.width + lx];
                if (colorIndex > 255) continue;
                const [r, g, b] = this.doc.palette.getColor(colorIndex);
                const off = (y * vw + x) * 4;
                buf[off] = r;
                buf[off + 1] = g;
                buf[off + 2] = b;
                buf[off + 3] = 255;
            }
        }

        const tmp = document.createElement('canvas');
        tmp.width = vw;
        tmp.height = vh;
        tmp.getContext('2d').putImageData(imgData, 0, 0);

        // Draw the visible portion at its correct position within the thumbnail
        ctx.drawImage(tmp, 0, 0, vw, vh,
            dx + Math.round(lx0 * scale), dy + Math.round(ly0 * scale),
            Math.round(vw * scale) || 1, Math.round(vh * scale) || 1);
    }

    // Rename the active layer (F2), mirroring the double-click behavior.
    startRenameActiveLayer() {
        const nameEl = this.list.querySelector('.layer-item.active .layer-name');
        if (!nameEl) return;
        const index = this.doc.activeLayerIndex;
        this._startRename(nameEl, this.doc.layers[index], index);
    }

    _startRename(nameEl, layer, layerIndex) {
        const beforeName = layer.name;
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'layer-name-input';
        input.value = layer.name;
        nameEl.replaceWith(input);
        input.focus();
        input.select();

        const commit = () => {
            const trimmed = input.value.trim();
            if (trimmed && trimmed !== beforeName) {
                layer.name = trimmed;
                this.undoManager.pushEntry({
                    type: 'layer-rename',
                    layerIndex,
                    beforeName,
                    afterName: trimmed,
                });
            }
            this.render();
        };

        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            if (e.key === 'Escape') { e.preventDefault(); this.render(); }
            e.stopPropagation();
        });
        input.addEventListener('blur', commit);
    }

    _addLayer() {
        const before = this._snapshotMeta();
        const layer = this.doc.addLayer();
        if (this.doc.animationEnabled) this.doc.saveCurrentFrame();
        const insertIndex = this.doc.activeLayerIndex;
        const after = this._snapshotMeta();
        this.undoManager.pushEntry({
            type: 'layer-add',
            insertIndex,
            layer,
            beforeActiveIndex: before.activeIndex,
            afterActiveIndex: after.activeIndex,
            beforeSelected: before.selected,
            afterSelected: after.selected,
            beforeAnim: before.anim,
            afterAnim: after.anim,
        });
        this.bus.emit('layer-changed');
    }

    _deleteLayer() {
        const layer = this.doc.layers[this.doc.activeLayerIndex];
        if (!confirm(`Delete layer "${layer.name}"?`)) return;
        const removedIndex = this.doc.activeLayerIndex;
        const before = this._snapshotMeta();
        if (this.doc.removeLayer(removedIndex)) {
            if (this.doc.animationEnabled) this.doc.saveCurrentFrame();
            const after = this._snapshotMeta();
            this.undoManager.pushEntry({
                type: 'layer-delete',
                removedIndex,
                layer,
                beforeActiveIndex: before.activeIndex,
                afterActiveIndex: after.activeIndex,
                beforeSelected: before.selected,
                afterSelected: after.selected,
                beforeAnim: before.anim,
                afterAnim: after.anim,
            });
            this.bus.emit('layer-changed');
        }
    }

    _moveLayer(dir) {
        const from = this.doc.activeLayerIndex;
        // dir=-1 means "up" visually = higher index in array
        const to = from - dir;
        const before = this._snapshotMeta();
        if (this.doc.moveLayer(from, to)) {
            if (this.doc.animationEnabled) this.doc.saveCurrentFrame();
            const after = this._snapshotMeta();
            this.undoManager.pushEntry({
                type: 'layer-move',
                fromIndex: from,
                toIndex: to,
                beforeActiveIndex: before.activeIndex,
                afterActiveIndex: after.activeIndex,
                beforeSelected: before.selected,
                afterSelected: after.selected,
            });
            this.bus.emit('layer-changed');
        }
    }

    _duplicateLayer() {
        const before = this._snapshotMeta();
        const copy = this.doc.duplicateLayer(this.doc.activeLayerIndex);
        if (this.doc.animationEnabled) this.doc.saveCurrentFrame();
        const insertIndex = this.doc.activeLayerIndex;
        const after = this._snapshotMeta();
        this.undoManager.pushEntry({
            type: 'layer-add',
            insertIndex,
            layer: copy,
            beforeActiveIndex: before.activeIndex,
            afterActiveIndex: after.activeIndex,
            beforeSelected: before.selected,
            afterSelected: after.selected,
            beforeAnim: before.anim,
            afterAnim: after.anim,
        });
        this.bus.emit('layer-changed');
    }
}
